import React, { useState } from "react";
import { Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";

interface QuestionOption {
  label: string;
  description: string;
}

interface ClarifyingQuestion {
  text: string;
  options: QuestionOption[];
  allow_other: boolean;
}

interface PlanQuestionCardProps {
  question: ClarifyingQuestion;
  questionNumber: number;
  maxQuestions: number;
  onSubmit: (answer: string) => Promise<void>;
}

export function PlanQuestionCard({
  question,
  questionNumber,
  maxQuestions,
  onSubmit,
}: PlanQuestionCardProps) {
  const [selectedOption, setSelectedOption] = useState<string>("");
  const [otherText, setOtherText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const isOther = selectedOption === "__other__";
  const canSubmit = selectedOption && (!isOther || otherText.trim().length > 0);

  const handleSubmit = async () => {
    if (!canSubmit || submitting) return;

    setSubmitting(true);
    try {
      const answer = isOther ? otherText.trim() : selectedOption;
      await onSubmit(answer);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className="border-border">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-medium text-foreground">
            {question.text}
          </CardTitle>
          <span className="text-xs text-muted-foreground">
            {questionNumber} of {maxQuestions}
          </span>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <RadioGroup
          value={selectedOption}
          onValueChange={setSelectedOption}
          className="space-y-3"
        >
          {question.options.map((option, idx) => (
            <div
              key={idx}
              className="flex items-start space-x-3 rounded-lg border border-border p-3 hover:bg-muted/50 transition-colors cursor-pointer"
              onClick={() => setSelectedOption(option.label)}
            >
              <RadioGroupItem
                value={option.label}
                id={`option-${idx}`}
                className="mt-0.5"
              />
              <Label
                htmlFor={`option-${idx}`}
                className="flex-1 cursor-pointer"
              >
                <span className="block text-sm font-medium text-foreground">
                  {option.label}
                </span>
                {option.description && (
                  <span className="block text-xs text-muted-foreground mt-0.5">
                    {option.description}
                  </span>
                )}
              </Label>
            </div>
          ))}

          {question.allow_other && (
            <div
              className={`flex items-start space-x-3 rounded-lg border p-3 transition-colors cursor-pointer ${
                isOther ? "border-primary bg-primary/5" : "border-border hover:bg-muted/50"
              }`}
              onClick={() => setSelectedOption("__other__")}
            >
              <RadioGroupItem
                value="__other__"
                id="option-other"
                className="mt-0.5"
              />
              <Label htmlFor="option-other" className="flex-1 cursor-pointer">
                <span className="block text-sm font-medium text-foreground">
                  Other
                </span>
                <span className="block text-xs text-muted-foreground mt-0.5">
                  Describe your own approach
                </span>
              </Label>
            </div>
          )}
        </RadioGroup>

        {isOther && (
          <Input
            placeholder="Describe what you'd like..."
            value={otherText}
            onChange={(e) => setOtherText(e.target.value)}
            className="mt-3"
            autoFocus
          />
        )}

        <Button
          onClick={handleSubmit}
          disabled={!canSubmit || submitting}
          className="w-full"
        >
          {submitting ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Submitting...
            </>
          ) : (
            "Continue"
          )}
        </Button>
      </CardContent>
    </Card>
  );
}
